import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import {
  fetchQueuePublic,
  subscribeToQueue,
  type QueueChannelStatus,
  type QueuePublicEntry,
} from '../services/queue';
import { NEXT_UP_CHANNEL, type NextUpSignal } from '../components/NextUpWidget';
import '../styles/queue-display.css';

// Customer-facing queue screen. One component, two shipping paths, one look:
//   - standalone Cloudflare Pages build (VITE_DISPLAY_ONLY=true), a TV pointed
//     at neoseum-queue[-staging].pages.dev/?locationId=…
//   - the Electron second screen at #/queue-display?locationId=…
//
// Privacy: BOTH paths read booking.fn_get_queue_public and nothing else. That
// payload is already PII-minimal (a numbered surname, no first names, no
// booking numbers, no ids), and it is the server that decides what a visitor
// is called — this file renders display_name verbatim and never assembles a
// name of its own. The operator payload (fn_get_queue) must never reach a TV.
//
// Contract: RealityDatabase/supabase/migrations/
//   20260910130237_booking_queue_eta_in_progress_and_public_names.sql

/** How long the "kom naar voren" takeover owns the screen. */
const ANNOUNCE_MS = 6000;
/** Never let a burst of takeovers lock the list away for a minute. */
const ANNOUNCE_MAX_PENDING = 3;
/** More rows than this vanishing between two refreshes is not a dispatch, it
 *  is the queue being cleared (end of day, cron cleanup, a location switch).
 *  A single dispatch is one visitor, or one party launched together. */
const ANNOUNCE_MAX_BATCH = 6;
/** Rendered before the first fit measurement, and the floor afterwards. */
const DEFAULT_ROWS_PER_COLUMN = 8;
const MIN_VISIBLE_ROWS = 3;
/** The list is column-major over at most this many columns. Two is a reading
 *  decision, not a fitting one: a third column would halve the name again. */
const MAX_COLUMNS = 2;
/** Poll cadence while the realtime channel is not actually delivering. */
const FALLBACK_POLL_MS = 15_000;
/** Safety poll that runs no matter what the channel says. */
const SAFETY_POLL_MS = 60_000;
/** Collapses a per-person enqueue (N inserts in one txn) into one refresh. */
const REALTIME_DEBOUNCE_MS = 300;
/** How rows glide up when the person in front of them is called forward. */
const FLIP_TRANSITION = 'transform 460ms cubic-bezier(0.16, 1, 0.3, 1)';

// Vite is configured with base "./" so the same bundle works from a Pages root
// and from Electron's file:// URL. Assets live in frontend/public/experiences.
const ASSET_BASE = import.meta.env.BASE_URL || './';
const HOUSE_LOGO = `${ASSET_BASE}experiences/neoseum-logo.svg`;

/** booking_slug -> transparent experience logo in frontend/public/experiences.
 *  Deliberately a lookup and not a template: an experience whose slug we have
 *  no artwork for (a new title, a null booking_slug) falls back to the marketed
 *  name on its own, which is a complete row, not a broken image. */
const EXPERIENCE_LOGOS: Record<string, string> = {
  pharaohs: 'pharaohs.webp',
  odyssey: 'odyssey.webp',
  maya: 'maya.webp',
};

function logoForEntry(entry: QueuePublicEntry): string | null {
  const slug = entry.experiences?.[0]?.slug?.trim().toLowerCase();
  if (!slug) return null;
  const file = EXPERIENCE_LOGOS[slug];
  return file ? `${ASSET_BASE}experiences/${file}` : null;
}

/** The MARKETED name in the visitor's play language ("Voyage of the Pharaohs").
 *  experiences[].name already is that; experience_names is the pre-joined
 *  fallback for a payload that predates the per-pick array. */
function experienceLabel(entry: QueuePublicEntry): string {
  const picks = (entry.experiences ?? []).map((x) => x.name).filter(Boolean);
  if (picks.length > 0) return picks.join(' + ');
  return entry.experience_names ?? '';
}

const LANGUAGE_CODES: Record<string, string> = { nl: 'NL', en: 'EN', de: 'DE' };

function languageCode(entry: QueuePublicEntry): string | null {
  const raw = (entry.play_language ?? '').trim().toLowerCase();
  if (!raw) return null;
  return LANGUAGE_CODES[raw] ?? raw.toUpperCase();
}

/** Which codes LanguageFlag actually draws. The row asks BEFORE rendering so
 *  a language we have no flag for shows its two letters instead of a gap. */
const FLAG_CODES = new Set(['nl', 'en', 'de']);

function hasFlagArt(code: string | null): boolean {
  return FLAG_CODES.has((code ?? '').trim().toLowerCase());
}

/** "nog ~15 min" / "nu" / '' — Dutch, visitor-facing. The operator widgets keep
 *  their own terser formatEta() in services/queue.ts. */
function etaLabel(entry: QueuePublicEntry): string | null {
  if (entry.parked) return null;
  const m = entry.eta_minutes;
  if (m === null || m === undefined) return null;
  // The head already says "Nu aan de beurt"; only add minutes when the ETA
  // genuinely still has some, which happens when every PC is mid-run.
  if (m <= 0) return entry.is_next ? null : 'nu';
  return `nog ~${m} min`;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function readLocationFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  // Hash variant (Electron secondary window): #/queue-display?locationId=…
  const hash = window.location.hash;
  const qIdx = hash.indexOf('?');
  if (qIdx >= 0) {
    const fromHash = new URLSearchParams(hash.slice(qIdx + 1)).get('locationId');
    if (fromHash) return fromHash;
  }
  // Plain query variant (standalone display-only deploy):
  // https://queue.neoseum.com/?locationId=…
  return new URLSearchParams(window.location.search).get('locationId');
}

/** One "kom naar voren" takeover, derived purely from the payload diff. */
interface Announcement {
  /** Unique per firing so React remounts and the entrance animation replays. */
  key: string;
  name: string;
  experience: string;
  logo: string | null;
  /** Party members who were started in the same refresh, minus the leader. */
  others: number;
}

/**
 * Who to announce, from two consecutive snapshots.
 *
 * The trigger is `called`: the operator opened "Volgende bezoeker" for this
 * visitor and is waiting for them at the desk. That is deliberately EARLIER
 * than the launch (Dirk, 2026-09-10) - the visitor has to walk up first, so
 * their experience and language can be checked with them before anything
 * starts. Announcing at launch called people forward after the fact.
 *
 * Rows are grouped by party_key so a party called together produces ONE
 * takeover (the member with the lowest position, plus "en N anderen") instead
 * of a stampede, and an oversized batch is treated as a queue reset rather
 * than a call so an end-of-day wipe never hijacks the screen.
 *
 * Fallback: a database that does not carry `called` yet (the migration is not
 * applied) keeps the old behaviour, announcing whoever left the list, so the
 * screen is never silent in the window between a deploy and a push.
 */
function announcementsFor(
  previous: QueuePublicEntry[],
  next: QueuePublicEntry[],
  seq: number,
): Announcement[] {
  // `called` absent on every row = a pre-migration database; fall back to the
  // rows that left the list so the display keeps working either way.
  const hasCalled = next.some((e) => e.called !== undefined);
  let subjects: QueuePublicEntry[];
  if (hasCalled) {
    const calledBefore = new Set(previous.filter((e) => e.called).map((e) => e.queue_entry_id));
    subjects = next.filter((e) => e.called && !calledBefore.has(e.queue_entry_id));
  } else {
    const stillQueued = new Set(next.map((e) => e.queue_entry_id));
    subjects = previous.filter((e) => !stillQueued.has(e.queue_entry_id));
  }
  if (subjects.length === 0 || subjects.length > ANNOUNCE_MAX_BATCH) return [];

  const parties = new Map<string, QueuePublicEntry[]>();
  for (const entry of subjects) {
    const key = entry.party_key || entry.queue_entry_id;
    const bucket = parties.get(key);
    if (bucket) bucket.push(entry);
    else parties.set(key, [entry]);
  }

  return Array.from(parties.values())
    .map((members) => {
      const [leader] = [...members].sort((a, b) => a.position - b.position);
      return {
        key: `${leader.queue_entry_id}:${seq}`,
        name: leader.display_name,
        experience: experienceLabel(leader),
        logo: logoForEntry(leader),
        others: members.length - 1,
        position: leader.position,
      };
    })
    .sort((a, b) => a.position - b.position)
    .map(({ position: _position, ...announcement }) => announcement);
}

/** What one measuring pass found the panel can hold. Both halves are measured
 *  from the live layout — height buys rows, width buys columns — so the same
 *  bundle fits a 4K TV, a 720p panel and an Electron window of any size. */
interface ListFit {
  rowsPerColumn: number;
  columns: number;
}

export default function QueueDisplay() {
  // On a one-screen venue this page covers the operator's own touchscreen, and
  // the window is frameless, fullscreen and always on top, so a tap has to be
  // the way out. On a real second screen it must NOT close, or a visitor
  // leaning on the TV would wipe the board.
  const [tapToClose, setTapToClose] = useState(false);

  const [locationId, setLocationId] = useState<string | null>(() => readLocationFromUrl());
  const [rows, setRows] = useState<QueuePublicEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [channelStatus, setChannelStatus] = useState<QueueChannelStatus>('CONNECTING');
  const [nextUp, setNextUp] = useState<NextUpSignal | null>(null);
  const [pending, setPending] = useState<Announcement[]>([]);
  const [fit, setFit] = useState<ListFit>({ rowsPerColumn: DEFAULT_ROWS_PER_COLUMN, columns: 1 });

  const nextUpTimerRef = useRef<number | null>(null);
  // Last snapshot we rendered. null means "no baseline" — the first load after
  // mount or after a location switch, where nothing may be announced.
  const previousRef = useRef<QueuePublicEntry[] | null>(null);
  const announceSeqRef = useRef(0);
  // Guards against a slow response for a location the screen has left.
  const locationRef = useRef<string | null>(locationId);
  locationRef.current = locationId;

  // The Electron main process can push a new (locationId, roomId) when the
  // operator switches rooms on the dashboard — we re-subscribe in place
  // rather than reloading the window.
  useEffect(() => {
    if (!window.electron?.queueDisplay?.onContextChange) return;
    const dispose = window.electron.queueDisplay.onContextChange((ctx) => {
      setLocationId(ctx.locationId ?? null);
    });
    return () => {
      if (typeof dispose === 'function') dispose();
    };
  }, []);

  // "Volgende: …" heads-up pushed from PCTile when last_level_warning fires.
  // Two delivery paths so both deployment topologies work:
  //   - Electron secondary window: IPC via window.electron.queueDisplay
  //   - Standalone tab / public web deploy: BroadcastChannel
  useEffect(() => {
    function showOverlay(signal: NextUpSignal) {
      setNextUp(signal);
      if (nextUpTimerRef.current !== null) {
        window.clearTimeout(nextUpTimerRef.current);
      }
      // 10s auto-dismiss; customer / operator can tap to close earlier.
      nextUpTimerRef.current = window.setTimeout(() => setNextUp(null), 10_000);
    }

    const ipcDispose = window.electron?.queueDisplay?.onNextUp?.((s) => showOverlay(s));

    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel(NEXT_UP_CHANNEL);
      bc.onmessage = (ev) => showOverlay(ev.data as NextUpSignal);
    } catch {
      // BroadcastChannel unsupported — IPC path still works in Electron.
    }

    return () => {
      if (typeof ipcDispose === 'function') ipcDispose();
      if (bc) bc.close();
      if (nextUpTimerRef.current !== null) {
        window.clearTimeout(nextUpTimerRef.current);
      }
    };
  }, []);

  // Every refresh path (initial, realtime, both polls) lands here, so the
  // "who disappeared" baseline can never drift out of sync with the list.
  const applySnapshot = useCallback((next: QueuePublicEntry[]) => {
    const previous = previousRef.current;
    previousRef.current = next;
    setRows(next);
    if (!previous || previous.length === 0) return;
    const seq = (announceSeqRef.current += 1);
    const fresh = announcementsFor(previous, next, seq);
    if (fresh.length === 0) return;
    // Keep whatever is already on screen; drop the overflow at the tail.
    setPending((queued) => [...queued, ...fresh].slice(0, ANNOUNCE_MAX_PENDING));
  }, []);

  const load = useCallback(async () => {
    const forLocation = locationRef.current;
    if (!forLocation) {
      previousRef.current = null;
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      const snapshot = await fetchQueuePublic(forLocation);
      if (locationRef.current !== forLocation) return;
      applySnapshot(snapshot.entries);
    } catch (err) {
      console.error('QueueDisplay load failed', err);
    } finally {
      if (locationRef.current === forLocation) setLoading(false);
    }
  }, [applySnapshot]);

  useEffect(() => {
    const api = window.electron?.queueDisplay;
    if (!api?.onPlacement) return;
    const off = api.onPlacement((p) => setTapToClose(!!p?.onPrimary));
    return off;
  }, []);

  // Initial load + realtime subscription + 60s safety poll, per location.
  useEffect(() => {
    // A different room is a different queue: forget the baseline so nobody
    // from the old room is announced as "kom naar voren".
    previousRef.current = null;
    setPending([]);
    setLoading(true);
    void load();

    if (!locationId) return;

    let debounceT: ReturnType<typeof setTimeout> | null = null;
    const unsub = subscribeToQueue(
      locationId,
      () => {
        if (debounceT) clearTimeout(debounceT);
        debounceT = setTimeout(() => { void load(); }, REALTIME_DEBOUNCE_MS);
      },
      (status) => {
        setChannelStatus(status);
        // Catch up on anything missed while the socket was down.
        if (status === 'SUBSCRIBED') void load();
      },
    );
    const safety = window.setInterval(() => void load(), SAFETY_POLL_MS);
    return () => {
      if (debounceT) clearTimeout(debounceT);
      unsub();
      window.clearInterval(safety);
    };
  }, [locationId, load]);

  // While the realtime channel is not actually delivering (connecting, timed
  // out, errored), poll every 15 s so a TV with a flaky socket still moves.
  // Stops the moment the channel reports SUBSCRIBED.
  useEffect(() => {
    if (!locationId || channelStatus === 'SUBSCRIBED') return;
    const t = window.setInterval(() => void load(), FALLBACK_POLL_MS);
    return () => window.clearInterval(t);
  }, [locationId, channelStatus, load]);

  // Retire the takeover on screen after ANNOUNCE_MS. Keyed on the head's own
  // key so a second announcement arriving mid-run does not restart the timer.
  const announcement = pending[0] ?? null;
  const announcementKey = announcement?.key ?? null;
  useEffect(() => {
    if (!announcementKey) return;
    const t = window.setTimeout(() => setPending((queued) => queued.slice(1)), ANNOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [announcementKey]);

  // --- fitting the list to the panel ---------------------------------------
  // The TV never scrolls. .qd-list is flex:1 with min-height:0, so its height
  // is independent of its content and one measuring pass is stable: rows have
  // a fixed height in CSS, and the "+ nog N wachtenden" line is always in the
  // DOM (invisible when unused) so the reserve never changes underneath us.
  //
  // Height buys ROWS PER COLUMN, width buys COLUMNS, visible = the product.
  // The column threshold is measured too — a hidden .qd-probe carries the
  // CSS token --qd-col-min, so "is this panel wide enough for two" is asked
  // of the real layout instead of a media query on the viewport, which the
  // Electron second screen (any size, any aspect) would get wrong.
  const listRef = useRef<HTMLDivElement | null>(null);
  const rowsRef = useRef<HTMLDivElement | null>(null);
  const moreRef = useRef<HTMLDivElement | null>(null);
  const probeRef = useRef<HTMLDivElement | null>(null);

  const measure = useCallback(() => {
    const list = listRef.current;
    const rowsEl = rowsRef.current;
    if (!list || !rowsEl) return;
    const firstRow = rowsEl.querySelector<HTMLElement>('.qd-row');
    if (!firstRow) return;
    const rowHeight = firstRow.offsetHeight;
    if (rowHeight <= 0) return;

    const outerGap = parseFloat(window.getComputedStyle(list).rowGap) || 0;
    const gridStyle = window.getComputedStyle(rowsEl);
    const rowGap = parseFloat(gridStyle.rowGap) || 0;
    const columnGap = parseFloat(gridStyle.columnGap) || 0;

    const reserve = moreRef.current ? moreRef.current.offsetHeight + outerGap : 0;
    const available = list.clientHeight - reserve;
    const fits = Math.floor((available + rowGap) / (rowHeight + rowGap));
    const rowsPerColumn = Math.max(MIN_VISIBLE_ROWS, fits);

    // Fractional width, not offsetWidth: --qd-col-min is a calc() of clamps
    // and rounding it down would flip the count a pixel early.
    const minColumn = probeRef.current
      ? probeRef.current.getBoundingClientRect().width
      : 0;
    const columns =
      minColumn > 0
        ? Math.min(
            MAX_COLUMNS,
            Math.max(1, Math.floor((rowsEl.clientWidth + columnGap) / (minColumn + columnGap))),
          )
        : 1;

    // Same object when nothing changed: a ResizeObserver that always set
    // state would re-render on its own layout write forever.
    setFit((prev) =>
      prev.rowsPerColumn === rowsPerColumn && prev.columns === columns
        ? prev
        : { rowsPerColumn, columns },
    );
  }, []);

  const hasRows = rows.length > 0;

  useLayoutEffect(() => { measure(); }, [measure, rows.length]);

  useEffect(() => {
    if (!hasRows) return;
    const list = listRef.current;
    if (!list || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    // Covers window resize and the portrait/landscape flip in one observer.
    const observer = new ResizeObserver(() => measure());
    observer.observe(list);
    let cancelled = false;
    // Row height is fixed in CSS, but the overflow line is not — remeasure
    // once the brand webfonts have swapped in.
    document.fonts?.ready
      .then(() => { if (!cancelled) measure(); })
      .catch(() => { /* no Font Loading API — the first pass stands */ });
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [measure, hasRows]);

  // --- FLIP: rows that move when someone is called forward ------------------
  // First: invert (hold every moved row at its old offset with no transition).
  // Then, one frame later: play (drop the transform under a transition). The
  // held rows live in a ref rather than the effect closure so a re-render that
  // lands between the two halves cannot strand a row at its old offset.
  // Rows are found by querying the list for data-qd-key rather than by per-row
  // ref callbacks: an inline `ref={el => …}` gets a new identity every render,
  // so React would detach (null) and reattach each row before the layout
  // effect runs, wiping the very baseline the diff needs.
  //
  // BOTH axes: with two columns a row does not only glide up, it also jumps
  // from the top of the right column to the bottom of the left one as the
  // queue drains. Recording offsetTop alone would teleport that one sideways.
  const rowOffsetsRef = useRef(new Map<string, { top: number; left: number }>());
  const flipRafRef = useRef<number | null>(null);
  const flipHeldRef = useRef<HTMLElement[]>([]);

  const playFlip = useCallback(() => {
    if (flipRafRef.current !== null) {
      window.cancelAnimationFrame(flipRafRef.current);
      flipRafRef.current = null;
    }
    const held = flipHeldRef.current;
    if (held.length === 0) return;
    flipHeldRef.current = [];
    for (const el of held) {
      el.style.transition = FLIP_TRANSITION;
      el.style.transform = '';
    }
  }, []);

  useLayoutEffect(() => {
    // Release anything still held from a previous pass before measuring.
    playFlip();

    const list = listRef.current;
    if (!list) {
      // Idle/empty state: drop the baseline so returning to the list does not
      // animate every row from a stale offset.
      rowOffsetsRef.current = new Map();
      return;
    }

    const reduce = prefersReducedMotion();
    const offsets = new Map<string, { top: number; left: number }>();
    const moved: { el: HTMLElement; dx: number; dy: number }[] = [];
    list.querySelectorAll<HTMLElement>('.qd-row[data-qd-key]').forEach((el) => {
      const key = el.dataset.qdKey as string;
      // offsets, not getBoundingClientRect: they ignore in-flight transforms,
      // so a FLIP interrupted by the next refresh still measures the truth.
      // Both are relative to .qd-list, the one positioned ancestor, so they
      // stay comparable across a column change.
      const top = el.offsetTop;
      const left = el.offsetLeft;
      offsets.set(key, { top, left });
      const was = rowOffsetsRef.current.get(key);
      if (!reduce && was && (was.top !== top || was.left !== left)) {
        moved.push({ el, dx: was.left - left, dy: was.top - top });
      }
    });
    rowOffsetsRef.current = offsets;
    if (moved.length === 0) return;

    for (const { el, dx, dy } of moved) {
      el.style.transition = 'none';
      el.style.transform = `translate(${dx}px, ${dy}px)`;
    }
    flipHeldRef.current = moved.map((m) => m.el);
    flipRafRef.current = window.requestAnimationFrame(() => playFlip());
  });

  const closeOnTap = useCallback(() => {
    if (!tapToClose) return;
    void window.electron?.queueDisplay?.close?.();
  }, [tapToClose]);

  function dismissNextUp() {
    setNextUp(null);
    if (nextUpTimerRef.current !== null) {
      window.clearTimeout(nextUpTimerRef.current);
      nextUpTimerRef.current = null;
    }
  }

  // The takeover outranks the "Volgende: …" heads-up; only ever one overlay.
  const overlay = announcement ? (
    <CalledForward key={announcement.key} announcement={announcement} />
  ) : nextUp ? (
    <NextUpBanner signal={nextUp} onDismiss={dismissNextUp} />
  ) : null;

  if (!locationId) {
    return (
      <div className="qd qd--idle" onClick={closeOnTap}>
        <img className="qd-idle-logo" src={HOUSE_LOGO} alt="Neoseum" />
        <div className="qd-idle-message">Geen locatie geselecteerd.</div>
      </div>
    );
  }

  if (loading && rows.length === 0) {
    return (
      <div className="qd qd--idle" onClick={closeOnTap}>
        <img className="qd-idle-logo" src={HOUSE_LOGO} alt="Neoseum" />
        <div className="qd-idle-message">Wachtrij laden…</div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="qd qd--idle" onClick={closeOnTap}>
        <img className="qd-idle-logo" src={HOUSE_LOGO} alt="Neoseum" />
        <div className="qd-idle-message">Welkom, er is nu geen wachtrij</div>
        {overlay}
      </div>
    );
  }

  // Capacity is the measured product; MIN_VISIBLE_ROWS is the floor a cramped
  // panel still renders (and clips) rather than showing an empty list.
  const capacity = Math.max(MIN_VISIBLE_ROWS, fit.rowsPerColumn * fit.columns);
  const shown = rows.slice(0, capacity);
  const hidden = rows.length - shown.length;

  // A second column only once the queue actually outgrows the first: five
  // people read better as one full-width column than as two half-width stubs.
  // Once it is in use the rows split evenly, so the right column is never a
  // single orphan under a full left one.
  const columns = fit.columns > 1 && shown.length > fit.rowsPerColumn ? fit.columns : 1;
  const rowsPerColumn = Math.max(1, Math.ceil(shown.length / columns));
  const gridStyle = {
    '--qd-cols': String(columns),
    '--qd-rows-per-col': String(rowsPerColumn),
  } as CSSProperties;

  return (
    <div
      className="qd"
      onClick={closeOnTap}
      role={tapToClose ? 'button' : undefined}
      title={tapToClose ? 'Tik om te sluiten' : undefined}>
      <header className="qd-header">
        <img className="qd-logo" src={HOUSE_LOGO} alt="Neoseum" />
        <h1 className="qd-title">Wachtrij</h1>
        {/* Only on a single-screen venue, where this page is covering the
            operator's own touchscreen and a tap is the way back. */}
        {tapToClose && <span className="qd-tap-hint">Tik om te sluiten</span>}
        <div className="qd-header-spacer" />
        <div className="qd-count">
          {rows.length === 1 ? '1 wachtende' : `${rows.length} wachtenden`}
        </div>
      </header>

      <div className="qd-list" ref={listRef}>
        <div className="qd-rows" ref={rowsRef} style={gridStyle}>
          {shown.map((entry) => (
            <QueueRow key={entry.queue_entry_id} entry={entry} />
          ))}
        </div>
        {/* Outside the grid: it counts the whole queue, so it spans both
            columns — and stays a constant reserve for measure(). */}
        <div
          className={`qd-more ${hidden > 0 ? '' : 'qd-more--hidden'}`}
          ref={moreRef}
          aria-hidden={hidden > 0 ? undefined : true}
        >
          {hidden === 1 ? '+ nog 1 wachtende' : `+ nog ${hidden} wachtenden`}
        </div>
        {/* Carries --qd-col-min into JS as real pixels. See measure(). */}
        <div className="qd-probe" ref={probeRef} aria-hidden="true" />
      </div>

      <div className="qd-footer">
        Klopt je ervaring of taal niet, laat het dan weten aan onze medewerker zodra je{' '}
        <strong>aan de beurt</strong> bent.
      </div>

      {overlay}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface QueueRowProps {
  entry: QueuePublicEntry;
}

function QueueRow({ entry }: QueueRowProps) {
  const logo = logoForEntry(entry);
  const experience = experienceLabel(entry);
  const lang = languageCode(entry);
  const eta = etaLabel(entry);

  const classes = ['qd-row'];
  if (entry.is_next) classes.push('qd-row--head');
  if (entry.parked) classes.push('qd-row--parked');

  return (
    <div className={classes.join(' ')} data-qd-key={entry.queue_entry_id}>
      <div className="qd-pos">{entry.position}</div>
      {/* display_name comes from booking.fn_public_display_name: rendered
          verbatim, never rebuilt here. */}
      <div className="qd-name">{entry.display_name}</div>
      {/* Logo alone — the artwork IS the title. The marketed name rides on
          alt for a screen reader, and only becomes visible text for a slug we
          have no artwork for, where it carries the cell on its own. */}
      <div className="qd-exp">
        {logo ? (
          <img className="qd-exp-logo" src={logo} alt={experience} />
        ) : experience ? (
          <div className="qd-exp-name">{experience}</div>
        ) : null}
      </div>
      {/* Flag alone — nobody needs to be told the tricolour is "Nederlands".
          A code we draw no flag for keeps its two letters so the column never
          opens a hole. */}
      <div className="qd-lang">
        {hasFlagArt(entry.play_language) ? (
          <LanguageFlag code={entry.play_language} />
        ) : lang ? (
          <span className="qd-lang-code">{lang}</span>
        ) : null}
      </div>
      <div className="qd-status">
        {entry.parked ? (
          <span className="qd-eta qd-eta--parked">even weg</span>
        ) : (
          <>
            {entry.is_next ? <span className="qd-pill">Nu aan de beurt</span> : null}
            {eta ? <span className="qd-eta">{eta}</span> : null}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flags
// ---------------------------------------------------------------------------

/**
 * Inline SVG rather than emoji: the venue screens are Windows (Electron and
 * Chrome alike), and Windows has no regional-indicator glyphs — 🇳🇱 renders as
 * the bare letters "NL" there. These also stay crisp when scaled up for a TV.
 *
 * The aria-label is the language in its own name: the flag is now the only
 * language mark on the row, so the label is what a screen reader has to go on.
 * Every code drawn here must also be in FLAG_CODES.
 */
function LanguageFlag({ code }: { code: string | null }) {
  // Sanitised because useId() contains colons, which have no business in a
  // url(#…) reference.
  const clipId = `qd-flag-${useId().replace(/:/g, '')}`;
  const lang = (code ?? '').trim().toLowerCase();

  if (lang === 'nl') {
    return (
      <svg className="qd-flag" viewBox="0 0 9 6" role="img" aria-label="Nederlands">
        <rect width="9" height="6" fill="#ffffff" />
        <rect width="9" height="2" fill="#ae1c28" />
        <rect y="4" width="9" height="2" fill="#21468b" />
      </svg>
    );
  }

  if (lang === 'de') {
    return (
      <svg className="qd-flag" viewBox="0 0 5 3" role="img" aria-label="Deutsch">
        <rect width="5" height="3" fill="#000000" />
        <rect y="1" width="5" height="1" fill="#dd0000" />
        <rect y="2" width="5" height="1" fill="#ffce00" />
      </svg>
    );
  }

  if (lang === 'en') {
    return (
      <svg className="qd-flag" viewBox="0 0 60 30" role="img" aria-label="English">
        <clipPath id={clipId}>
          <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
        </clipPath>
        <rect width="60" height="30" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#ffffff" strokeWidth="6" />
        <path
          d="M0,0 L60,30 M60,0 L0,30"
          clipPath={`url(#${clipId})`}
          stroke="#c8102e"
          strokeWidth="4"
        />
        <path d="M30,0 v30 M0,15 h60" stroke="#ffffff" strokeWidth="10" />
        <path d="M30,0 v30 M0,15 h60" stroke="#c8102e" strokeWidth="6" />
      </svg>
    );
  }

  // Unknown code: the row renders .qd-lang-code instead (see hasFlagArt), so
  // this never leaves the language cell empty.
  return null;
}

// ---------------------------------------------------------------------------
// "Kom naar voren" takeover
// ---------------------------------------------------------------------------

function CalledForward({ announcement }: { announcement: Announcement }) {
  const style = { '--qd-announce-ms': `${ANNOUNCE_MS}ms` } as CSSProperties;
  return (
    <div className="qd-announce" style={style} role="alert">
      <div className="qd-announce-eyebrow">Aan de beurt</div>
      <div className="qd-announce-name">{announcement.name}</div>
      {announcement.logo ? (
        <img className="qd-announce-logo" src={announcement.logo} alt="" />
      ) : announcement.experience ? (
        <div className="qd-announce-exp">{announcement.experience}</div>
      ) : null}
      <div className="qd-announce-cta">Kom naar voren</div>
      {announcement.others > 0 ? (
        <div className="qd-announce-extra">
          {announcement.others === 1
            ? 'en nog 1 persoon uit je groep'
            : `en nog ${announcement.others} personen uit je groep`}
        </div>
      ) : null}
      <div className="qd-announce-bar" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// "Volgende: …" heads-up, pushed from a PC tile's last-level warning
// ---------------------------------------------------------------------------

interface NextUpBannerProps {
  signal: NextUpSignal;
  onDismiss: () => void;
}

function NextUpBanner({ signal, onDismiss }: NextUpBannerProps) {
  return (
    <div className="qd-nextup" role="status" onClick={onDismiss}>
      <span className="qd-nextup-label">Volgende</span>
      <span className="qd-nextup-name">{signal.displayName}</span>
    </div>
  );
}
